import React, { useEffect, useMemo, useState } from "react";
import { usePagination } from "../../hooks/usePagination";
import {
  fetchAdminUnits,
  fetchAdminRooms,
  fetchAdminProperties,
  fetchAdminLandlords,
  fetchAdminPropertyManagers,
  fetchAdminTenanciesAll,
  normalizeUnit,
  normalizeRoom,
} from "../../api/adminData";
import CreateLandlordInlineModal from "../../components/admin/CreateLandlordInlineModal";
import CreatePropertyManagerInlineModal from "../../components/admin/CreatePropertyManagerInlineModal";
import {
  parseMoneyChf,
  parseRoomPriceChf,
  roundCurrency,
  MODAL_COST_FIXED_SET,
  resolveModalCostBackendType,
} from "./apartments/apartmentsHelpers";
import UnitFormModal from "./apartments/UnitFormModal";
import UnitsKpiRow, { computeUnitKpiCounts } from "./apartments/UnitsKpiRow";
import UnitsToolbar from "./apartments/UnitsToolbar";
import UnitsPagination from "./apartments/UnitsPagination";
import {
  matchesUnitSearch,
  sortUnitList,
  DEFAULT_UNIT_SORT,
} from "./apartments/unitListCollection";
import UnitSegmentTabs, {
  DEFAULT_UNIT_SEGMENT,
  UnitSegmentPanel,
} from "./apartments/UnitSegmentTabs";
import BusinessApartmentsList from "./apartments/BusinessApartmentsList";
import CoLivingUnitsList from "./apartments/CoLivingUnitsList";
import { useUnitFormModal } from "./apartments/useUnitFormModal";
import {
  SEGMENT_BUSINESS_APARTMENT,
  SEGMENT_CO_LIVING,
} from "./apartments/unitListModel";

/** Rows per page. Fixed for now: a page-size selector is not this slice. */
const UNITS_PER_PAGE = 10;



function AdminApartmentsPage() {
  const [units, setUnits] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [tenancies, setTenancies] = useState(null);
  const [properties, setProperties] = useState([]);
  const [landlords, setLandlords] = useState([]);
  const [propertyManagers, setPropertyManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFetchError("");

    Promise.allSettled([
      fetchAdminUnits(),
      fetchAdminRooms(),
      fetchAdminProperties(),
      fetchAdminLandlords(),
      fetchAdminPropertyManagers(),
      fetchAdminTenanciesAll(),
    ]).then((results) => {
      if (cancelled) return;

      const [unitsRes, roomsRes, propsRes, landlordsRes, pmRes, tenRes] = results;

      if (unitsRes.status === "fulfilled") {
        const data = unitsRes.value;
        setUnits(Array.isArray(data) ? data.map(normalizeUnit) : []);
      } else {
        console.error(unitsRes.reason);
        setFetchError(
          unitsRes.reason?.message || "Einheiten konnten nicht geladen werden."
        );
        setUnits([]);
      }

      if (roomsRes.status === "fulfilled") {
        const data = roomsRes.value;
        setRooms(Array.isArray(data) ? data.map(normalizeRoom) : []);
      } else {
        console.error(roomsRes.reason);
        setRooms([]);
      }

      if (propsRes.status === "fulfilled") {
        const data = propsRes.value;
        setProperties(Array.isArray(data) ? data : []);
      } else {
        console.error(propsRes.reason);
        setProperties([]);
      }

      if (landlordsRes.status === "fulfilled") {
        const data = landlordsRes.value;
        setLandlords(Array.isArray(data) ? data : []);
      } else {
        console.error(landlordsRes.reason);
        setLandlords([]);
      }

      if (pmRes.status === "fulfilled") {
        const data = pmRes.value;
        setPropertyManagers(Array.isArray(data) ? data : []);
      } else {
        console.error(pmRes.reason);
        setPropertyManagers([]);
      }

      if (tenRes.status === "fulfilled") {
        const data = tenRes.value;
        setTenancies(Array.isArray(data) ? data : []);
      } else {
        console.error(tenRes.reason);
        setTenancies(null);
      }

      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, []);


  const [searchTerm, setSearchTerm] = useState("");
  const [propertyFilter, setPropertyFilter] = useState("");

  const modal = useUnitFormModal({
    units,
    landlords,
    propertyManagers,
    setUnits,
    setRooms,
    setTenancies,
  });

  /**
   * Search and the property filter, applied to every unit before the segments
   * are split. The matching rule itself lives in `unitListCollection` so the
   * one field this slice adds -- the visible short id -- is testable without
   * rendering a page.
   */
  const filteredUnits = useMemo(() => {
    let result = units;
    if (searchTerm.trim()) {
      result = result.filter((unit) =>
        matchesUnitSearch(unit, searchTerm, { rooms, tenancies, landlords })
      );
    }
    if (propertyFilter) {
      result = result.filter((unit) => String(unit.property_id || "") === String(propertyFilter));
    }
    return result;
  }, [units, searchTerm, propertyFilter, rooms, tenancies, landlords]);

  const apartmentUnits = filteredUnits.filter((item) => item.type === "Apartment");
  const coLivingUnits = filteredUnits.filter((item) => item.type === "Co-Living");

  /**
   * Which segment is on screen. Co-Living is the default because it is the
   * larger part of the portfolio. Deliberately not remembered across mounts —
   * no storage, no query param — so every visit opens on the same segment.
   */
  const [activeSegment, setActiveSegment] = useState(DEFAULT_UNIT_SEGMENT);

  /**
   * The numbers beside the tab labels. They read off the SAME arrays the tables
   * render, so a tab can never promise a count the panel below it does not
   * show: search and the property filter narrow both together.
   */
  const segmentCounts = useMemo(
    () => ({
      [SEGMENT_CO_LIVING]: coLivingUnits.length,
      [SEGMENT_BUSINESS_APARTMENT]: apartmentUnits.length,
    }),
    [coLivingUnits.length, apartmentUnits.length]
  );

  /** How the active segment is ordered. Not remembered across mounts. */
  const [sortId, setSortId] = useState(DEFAULT_UNIT_SORT);

  /**
   * Sorting happens per segment and AFTER the split, so each tab is ordered
   * on its own. "Standard" returns the source order untouched.
   */
  const sortedCoLiving = useMemo(
    () => sortUnitList(coLivingUnits, sortId, { rooms, tenancies }),
    [coLivingUnits, sortId, rooms, tenancies]
  );
  const sortedApartments = useMemo(
    () => sortUnitList(apartmentUnits, sortId, { rooms, tenancies }),
    [apartmentUnits, sortId, rooms, tenancies]
  );

  const activeUnits =
    activeSegment === SEGMENT_CO_LIVING ? sortedCoLiving : sortedApartments;

  /**
   * Paging is the LAST step, and deliberately so: the tab counts above read
   * off the segment arrays, so a tab says 14 while its first page shows ten.
   * A page is a view onto a population, not the population.
   */
  const {
    pageItems,
    page,
    pageCount,
    next: nextPage,
    prev: prevPage,
    setPage,
  } = usePagination(activeUnits, { pageSize: UNITS_PER_PAGE });

  /**
   * Anything that changes WHICH units are listed, or in what order, sends the
   * reader back to page one. `usePagination` already clamps a page that no
   * longer exists, but a re-sorted list keeps its length -- without this, page
   * three of a list just sorted by revenue would show the third page of a
   * different order and look like nothing happened.
   */
  useEffect(() => {
    setPage(0);
    // `setPage` is recreated each render; depending on it would reset the page
    // on every keystroke's re-render rather than on an actual change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, propertyFilter, sortId, activeSegment]);

  const total = activeUnits.length;
  const from = total === 0 ? 0 : page * UNITS_PER_PAGE + 1;
  const to = page * UNITS_PER_PAGE + pageItems.length;

  /**
   * The five figures above the list. Counting lives in `computeUnitKpiCounts`
   * so it can be tested without rendering, and so segment and occupancy keep
   * coming from the helpers that already own those rules.
   */
  const kpiCounts = useMemo(
    () => computeUnitKpiCounts(filteredUnits, rooms, tenancies),
    [filteredUnits, rooms, tenancies]
  );

  return (
    <div data-testid="admin-apartments-page" className="mx-auto w-full max-w-[1400px]">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="m-0 text-[19px] font-semibold text-foreground">Einheiten</h1>
          <p className="m-0 mt-1 max-w-2xl text-[12.5px] leading-snug text-muted-foreground">
            Verwalte hier alle vermietbaren Einheiten, also Apartments und Co-Living Units.
          </p>
        </div>
        <button
          type="button"
          onClick={modal.handleOpenCreateModal}
          className="shrink-0 whitespace-nowrap rounded-[10px] bg-primary px-4 py-2 text-[13px] font-semibold text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          + Unit hinzufügen
        </button>
      </header>

      <div className="mt-6">
      {loading && (
        <div className="rounded-[12px] border border-border bg-card p-8 text-center text-[13px] text-muted-foreground">
          Laden…
        </div>
      )}

      {!loading && fetchError && (
        /*
          THE TEXT IS NOT `text-destructive`, AND THAT IS DELIBERATE. In light,
          `--destructive` is `0 72% 55%` and ships with `--destructive-foreground:
          white` -- it is tuned as a FILL that carries white text, not as text on
          a light surface. Used as body copy on its own 10% tint it measured
          3.25:1, under the 4.5:1 an error message of all things has to clear.

          The fill and the border keep the token, because filling is what it is
          for, and they are what still marks this box as an error. Only the words
          fall back to `--foreground` -- plain body text on a tinted surface,
          which is what they are. It measures 13.43:1 light and 17.51:1 dark, the
          highest of any existing token here: `muted-foreground` would also pass
          at 4.75:1 but muting an error is the wrong signal, and
          `destructive-foreground` is white, which is 1.33:1 on this tint.
        */
        <div className="rounded-[12px] border border-destructive/30 bg-destructive/10 p-8 text-center text-[13px] text-foreground">
          {fetchError}
        </div>
      )}

      {!loading && !fetchError && units.length === 0 && (
        <div className="rounded-[12px] border border-border bg-card p-8 text-center text-[13px] text-muted-foreground">
          Keine Daten vorhanden
        </div>
      )}

      {!loading && !fetchError && units.length > 0 && (
        <>
          <div className="mb-[12px] flex items-center gap-2">
            <span className="text-[10px] font-medium uppercase tracking-[0.8px] text-muted-foreground">Kennzahlen</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <div className="mb-6">
            <UnitsKpiRow counts={kpiCounts} />
          </div>

          <div className="mb-[12px] flex items-center gap-2">
            <span className="text-[10px] font-medium uppercase tracking-[0.8px] text-muted-foreground">Suche &amp; Filter</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <UnitsToolbar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            propertyFilter={propertyFilter}
            onPropertyFilterChange={setPropertyFilter}
            properties={properties}
            sortId={sortId}
            onSortChange={setSortId}
          />

          {/*
            The two segments used to sit stacked, each under its own rule, with
            both tables on screen at once. They are now a choice: Co-Living
            first and open, Business Apartments one click away. Only the active
            panel renders, so the page stops asking the reader to scroll past
            one portfolio to reach the other.
          */}
          <UnitSegmentTabs
            value={activeSegment}
            onValueChange={setActiveSegment}
            counts={segmentCounts}
          >
            <UnitSegmentPanel segment={SEGMENT_CO_LIVING}>
              {/*
                Slice 6: the thirteen-column table became rows that fit the
                viewport. `coLivingUnits` is passed unchanged -- the list
                renders this population, it does not re-derive it.
              */}
              {/*
                `pageItems` is the ACTIVE segment's slice. Radix mounts only
                the selected panel, so whichever of these two renders is the
                one the slice belongs to.
              */}
              <CoLivingUnitsList
                units={pageItems}
                rooms={rooms}
                tenancies={tenancies}
              />
              <UnitsPagination
                page={page}
                pageCount={pageCount}
                total={total}
                from={from}
                to={to}
                onPrev={prevPage}
                onNext={nextPage}
              />
            </UnitSegmentPanel>

            <UnitSegmentPanel segment={SEGMENT_BUSINESS_APARTMENT}>
              {/*
                Slice 7: the last horizontally scrolling table on this page
                became the same rows Co-Living uses. `apartmentUnits` is passed
                unchanged -- the list renders this population, it does not
                re-derive it.
              */}
              <BusinessApartmentsList
                units={pageItems}
                rooms={rooms}
                tenancies={tenancies}
              />
              <UnitsPagination
                page={page}
                pageCount={pageCount}
                total={total}
                from={from}
                to={to}
                onPrev={prevPage}
                onNext={nextPage}
              />
            </UnitSegmentPanel>
          </UnitSegmentTabs>
        </>
      )}
      </div>

      <UnitFormModal
        modal={modal}
        units={units}
        properties={properties}
      />

      <CreateLandlordInlineModal
        open={modal.createLandlordModalOpen}
        onClose={() => modal.setCreateLandlordModalOpen(false)}
        onSuccess={(created) => {
          if (created?.id) {
            setLandlords((prev) => [...prev, created]);
            modal.setFormData((prev) => ({ ...prev, landlord_id: String(created.id) }));
          }
        }}
      />
      <CreatePropertyManagerInlineModal
        open={modal.createPmModalOpen}
        onClose={() => modal.setCreatePmModalOpen(false)}
        onSuccess={(created) => {
          if (created?.id) {
            setPropertyManagers((prev) => [...prev, created]);
            modal.setFormData((prev) => ({ ...prev, property_manager_id: String(created.id) }));
          }
        }}
      />
    </div>
  );
}

export default AdminApartmentsPage;
